import api from './api';

export interface Document {
  id: string;
  name: string;
  type: 'contract' | 'blueprint' | 'permit' | 'invoice' | 'report' | 'photo' | 'other';
  fileType: string;
  size: number;
  uploadedBy: string;
  uploadDate: string;
  lastModified: string;
  project?: string;
  category: string;
  tags: string[];
  status: 'active' | 'archived' | 'pending-review';
  version: string;
  filePath?: string;
  url?: string;
}

export interface DocumentFilters {
  type?: string;
  status?: string;
  category?: string;
  project?: string;
  search?: string;
  tags?: string[];
}

export interface DocumentStats {
  total: number;
  active: number;
  archived: number;
  pendingReview: number;
  totalSize: number;
  byType: Record<string, number>;
  byCategory: Record<string, number>;
}

export interface DocumentUpload {
  file: File;
  name?: string;
  type: Document['type'];
  category: string;
  project?: string;
  tags?: string[];
  description?: string;
  isPublic?: boolean;
  company?: string;
}



class DocumentsService {
  // Get all documents
  async getAll(filters?: DocumentFilters): Promise<Document[]> {
    const response = await api.get('/documents', { params: filters });
    const payload = response.data || {};
    const list = Array.isArray(payload) ? payload : (payload.documents || payload.data || []);
    // Map backend schema to the UI's Document interface
    const mapped: Document[] = list.map((doc: any) => {
      const typeRaw = String(doc.type || 'Other');
      const typeLower = typeRaw.toLowerCase();
      const typeMapped = ['contract','blueprint','permit','invoice','report','photo','other'].includes(typeLower)
        ? (typeLower as Document['type'])
        : 'other';

      const projectName = typeof doc.project === 'string'
        ? doc.project
        : (doc.project?.name || '');

      const tags = Array.isArray(doc.metadata?.tags) ? doc.metadata.tags : (doc.tags || []);

      const statusRaw = String(doc.workflow?.status || '').toLowerCase();
      const statusMapped: Document['status'] = statusRaw === 'archived'
        ? 'archived'
        : statusRaw === 'under review' || statusRaw === 'pending'
          ? 'pending-review'
          : 'active';

      return {
        id: doc._id || doc.id,
        name: doc.name || doc.title || doc.originalName || 'Untitled',
        type: typeMapped,
        fileType: doc.fileInfo?.extension || doc.fileInfo?.mimeType || 'file',
        size: doc.fileInfo?.fileSize || doc.size || 0,
        uploadedBy: (doc.createdBy?.fullName || doc.createdBy?.name || doc.createdBy?.email || ''),
        uploadDate: doc.createdAt || doc.uploadDate || '',
        lastModified: doc.updatedAt || doc.lastModified || '',
        project: projectName,
        category: doc.category || '',
        tags,
        status: statusMapped,
        version: doc.version?.versionString || doc.version || '1.0.0',
        filePath: doc.filePath,
        url: doc.fileInfo?.url || doc.url,
      } as Document;
    });
    return mapped;
  }

  // Get document by ID
  async getById(id: string): Promise<Document> {
    const response = await api.get(`/documents/${id}`);
    return response.data;
  }

  // Upload new document
  async upload(documentUpload: DocumentUpload): Promise<Document> {
    const formData = new FormData();
    formData.append('file', documentUpload.file);
    formData.append('type', documentUpload.type);
    formData.append('category', documentUpload.category);
    
    // Backend expects 'title' rather than 'name'
    if (documentUpload.name) formData.append('title', documentUpload.name);
    if (documentUpload.project) formData.append('project', documentUpload.project);
    if (documentUpload.tags) formData.append('tags', documentUpload.tags.join(','));
    if (documentUpload.description) formData.append('description', documentUpload.description);
    if (typeof documentUpload.isPublic === 'boolean') formData.append('isPublic', String(documentUpload.isPublic));

    // Prefer explicitly provided company; otherwise include from current user profile when available
    const explicitCompany = documentUpload.company;
    if (explicitCompany) {
      formData.append('company', String(explicitCompany));
    } else {
      try {
        const me = await api.get('/auth/me');
        const user = me?.data?.user || me?.data;
        const company = user?.company;
        const companyId = typeof company === 'object' ? (company?._id || company?.id) : company;
        if (companyId) {
          formData.append('company', String(companyId));
        }
      } catch (e) {
        // Non-blocking: if company cannot be determined here, backend will validate
        // and respond with a clear error; caller will surface message.
      }
    }

    // Backend route is POST /documents for uploads
    const response = await api.post('/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Update document metadata
  async update(id: string, document: Partial<Document>): Promise<Document> {
    const response = await api.put(`/documents/${id}`, document);
    return response.data;
  }

  // Delete document
  async delete(id: string): Promise<void> {
    try {
      await api.delete(`/documents/${id}`);
    } catch (err: any) {
      const status = err?.response?.status;
      // Treat 404 as a successful no-op (already deleted)
      if (status === 404) return;
      throw err;
    }
  }

  // Download document
  async download(id: string): Promise<Blob> {
    const response = await api.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Get document preview URL
  async getPreviewUrl(id: string): Promise<string> {
    const response = await api.get(`/documents/${id}/preview`);
    return response.data.url;
  }

  // Get documents statistics
  async getStats(): Promise<DocumentStats> {
    const response = await api.get('/documents/stats');
    return response.data;
  }

  // Get documents by project
  async getByProject(projectId: string): Promise<Document[]> {
    const response = await api.get(`/documents/project/${projectId}`);
    return response.data;
  }

  // Update document status
  async updateStatus(id: string, status: Document['status']): Promise<Document> {
    const response = await api.patch(`/documents/${id}/status`, { status });
    return response.data;
  }

  // Get document categories
  async getCategories(): Promise<string[]> {
    const response = await api.get('/documents/categories');
    return response.data;
  }

  // Get document types
  async getTypes(): Promise<string[]> {
    const response = await api.get('/documents/types');
    return response.data;
  }

  // Search documents
  async search(query: string): Promise<Document[]> {
    const response = await api.get('/documents/search', { params: { q: query } });
    return response.data;
  }

  // Bulk update documents
  async bulkUpdate(updates: { id: string; data: Partial<Document> }[]): Promise<Document[]> {
    const response = await api.patch('/documents/bulk', { updates });
    return response.data;
  }

  // Archive document
  async archive(id: string): Promise<Document> {
    const response = await api.patch(`/documents/${id}/archive`);
    return response.data;
  }

  // Restore archived document
  async restore(id: string): Promise<Document> {
    const response = await api.patch(`/documents/${id}/restore`);
    return response.data;
  }

  // Create new version of document
  async createVersion(id: string, file: File): Promise<Document> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/documents/${id}/version`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

const documentsService = new DocumentsService();
export default documentsService;